import React, { useEffect, useRef } from "react";
import {
  useNavigate,
  useLocation,
  Navigate,
  Routes,
  Route,
} from "react-router-dom";
import Header from "../components/HomeHeader/Header";
import DictionaryPage from "./DictionaryPage";
import CoursesPage from "./CoursesPage";
import LearnPage from "./LearnPage";
import NavBar from "../components/NavBar/NavBar";
import { callWithAuth, getUserInfo } from "@/api/Api";
import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import GrowPage from "./GrowPage";
import { setPostHogResetFunction } from "@/api/login";
import ProfilePage from "./ProfilePage";
import { ActivePage } from "../components/NavBar/NavBar";

const HomeLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userInfo = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => callWithAuth(getUserInfo),
  });

  const runOnce = useRef(false);
  const posthog = usePostHog();
  
  // Scroll to the top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const distinctId = posthog.get_distinct_id();
    const alreadyIdentified = userInfo.data?.id.toString() === distinctId;
    const isTemp =
      userInfo.data?.isTemp ||
      userInfo.data?.isTempTrial ||
      userInfo.data?.isTempPremium;
    if (userInfo.data && !runOnce.current && !alreadyIdentified && !isTemp) {
      runOnce.current = true;
      posthog.identify(userInfo.data.id.toString(), {
        name: userInfo.data.firstName,
        email: userInfo.data.email,
        isTrial: userInfo.data.isTrial,
        isPremium: userInfo.data.isPremium,
        isFree: userInfo.data.isFree,
      });
      setPostHogResetFunction(() => {
        if (posthog) {
          posthog.reset();
          try {
            posthog.stopSessionRecording();
          } catch (error) {
            console.error(error);
          }
        }
      });
    }
    try {
      posthog.startSessionRecording(true);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const getActivePage = (): ActivePage => {
    const path = location.pathname.split("/").pop();
    const activePages: ActivePage[] = [
      "learn",
      "dictionary",
      "profile",
      "grow",
      "courses",
    ];
    if (activePages.includes(path as ActivePage)) {
      return path as ActivePage;
    }
    console.warn(`Invalid path: ${path}`);
    return "learn";
  };

  const activePage = getActivePage();

  if (userInfo.data?.isTemp) {
    return <Navigate to="/" />;
  }

  return (
    <div className="appPadding flex flex-col h-full w-full pt-1">
      <div className="fixed top-0 left-0 w-full h-16 items-center z-10 bg-inherit">
        <Header activePage={activePage} />
      </div>
      <div className="flex flex-col justify-top w-full pb-24">
        <Routes>
          <Route path="learn" element={<LearnPage />} />
          <Route path="dictionary" element={<DictionaryPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="courses" element={<CoursesPage />} />
          <Route path="grow" element={<GrowPage />} />
        </Routes>
      </div>
      <nav className="fixed bottom-0 left-0 w-full z-10">
        <NavBar navigate={navigate} activePage={activePage} />
      </nav>
    </div>
  );
};

export default HomeLayout;
